/**
 * Curated podcasts, books and free organisations for sports families —
 * /resources/podcasts-and-books-for-sports-parents.
 *
 * Curated means checked: every podcast was confirmed to exist with a real back
 * catalogue and a recent episode (or is labelled as an archive) as of
 * LISTEN_READ_LAST_VERIFIED. No affiliate links — we are not paid by anyone on
 * this list, and books deliberately carry no store link. Shows that couldn't be
 * confirmed (a "TrueSport podcast", a "Project Play podcast") were left out
 * rather than guessed at.
 */
export const LISTEN_READ_LAST_VERIFIED = '2026-10-01';

export interface CuratedItem {
  name: string;
  /** Host(s) for a podcast, author for a book, nothing for an org. */
  by?: string;
  url?: string;
  blurb: string;
  /** Why it matters for a family paying for a sport. */
  whyItMatters?: string;
  /** Book year, or podcast status ("Weekly", "Back catalogue"). */
  meta?: string;
}

export const podcasts: CuratedItem[] = [
  {
    name: 'Way of Champions',
    by: "John O'Sullivan & Dr. Jerry Lynch",
    url: 'https://podcasts.apple.com/us/podcast/way-of-champions-podcast/id1223779199',
    blurb: 'Weekly conversations with elite coaches and athletes about culture, values and athlete-centred development — from the Changing the Game Project. 500+ episodes since 2017.',
    whyItMatters: 'Helps you judge whether the club or coach you are paying for is actually developing your kid.',
    meta: 'Weekly',
  },
  {
    name: 'Raising Athletes',
    by: 'Kirsten Jones',
    url: 'https://podcasts.apple.com/us/podcast/raising-athletes/id1340521140',
    blurb: 'Sports-parenting interviews on confidence, resilience, comparison and how to behave on the sideline.',
    whyItMatters: 'Practical words for the car ride home after a tough game.',
    meta: 'Active, 190+ episodes',
  },
  {
    name: 'Youth Inc. with Greg Olsen',
    by: 'Greg Olsen',
    url: 'https://podcasts.apple.com/us/podcast/youth-inc-with-greg-olsen/id1610647237',
    blurb: 'The former NFL tight end on the changing youth-sports landscape: pressure, specialization, identity and injury.',
    whyItMatters: 'Strong on whether travel teams and private training are worth what they cost.',
    meta: 'Seasonal',
  },
  {
    name: 'The Pure Athlete Podcast',
    by: 'Jeff Francoeur, Britt Lee & Brad Williams',
    blurb: 'Long-term athlete development and a healthier youth-sports culture, with guests like Project Play’s Tom Farrey.',
    whyItMatters: 'Covers expensive trends — reclassifying, early specialization — before you sign up for them.',
    meta: '170+ episodes',
  },
  {
    name: 'Healthy Sports Parents',
    by: 'Jonathan Carone',
    url: 'https://podcasts.apple.com/us/podcast/healthy-sports-parents/id1781316422',
    blurb: 'Using youth sports to raise good people and strengthen families, not just to win.',
    whyItMatters: 'Asks whether chasing the next team or camp is worth it for your family.',
    meta: 'Since 2024',
  },
  {
    name: 'High Performance Mindset',
    by: 'Dr. Cindra Kamphoff',
    url: 'https://podcasts.apple.com/us/podcast/id1034819901',
    blurb: 'Short episodes on mental skills — focus, self-talk, handling pressure — from a mental performance coach.',
    whyItMatters: 'Mental-skills training without paying for a private sport psychologist.',
    meta: '750+ episodes',
  },
  {
    name: 'Finding Mastery',
    by: 'Dr. Michael Gervais',
    url: 'https://findingmastery.com',
    blurb: 'High-performance psychology interviews with elite athletes and thinkers.',
    whyItMatters: 'Best for older teens who are serious about their sport.',
    meta: 'Weekly',
  },
  {
    name: 'Healthy Youth Sports',
    by: 'Eric Utterback (ACSM)',
    url: 'https://acsm.org/nyshsi/podcast',
    blurb: 'Experts from the American College of Sports Medicine on injury prevention, sleep, nutrition and heat safety.',
    whyItMatters: 'Medical-grade advice that can head off the most expensive season of all — an injured one.',
    meta: 'Biweekly',
  },
  {
    name: 'Youth Sports Positive',
    by: 'Kelsey & Mark Dennehy',
    url: 'https://youthsportspositive.podbean.com/',
    blurb: 'A father-daughter show on youth and college sports news with a "work hard, have fun" lens.',
    whyItMatters: 'Explains NIL and transfer-portal changes in plain language.',
  },
  {
    name: 'Champion in Every Corner',
    by: 'U.S. Center for SafeSport',
    url: 'https://safesport.buzzsprout.com/1164980',
    blurb: 'Parent-facing safeguarding: talking with your athlete, warning signs of misconduct, online safety.',
    whyItMatters: 'Free safeguarding basics before you hand your child to a coach.',
    meta: 'Archive',
  },
];
export const books: CuratedItem[] = [
  {
    name: "The Most Expensive Game in Town: The Rising Cost of Youth Sports and the Toll on Today's Families",
    by: 'Mark Hyman',
    meta: '2012',
    blurb: 'How the youth-sports economy — companies, entrepreneurs, private coaches — drives what families pay.',
    whyItMatters: 'The most on-topic book there is if you are wondering where the money goes.',
  },
  {
    name: "Take Back the Game: How Money and Mania Are Ruining Kids' Sports — and Why It Matters",
    by: 'Linda Flanagan',
    meta: '2022',
    blurb: "How the industry profits from parents' college anxiety, and what that does to kids, injury rates and access.",
    whyItMatters: 'Worth reading before the next travel-team check.',
  },
  {
    name: 'Changing the Game',
    by: "John O'Sullivan",
    meta: '2013',
    blurb: 'Kids perform best when the sport stays theirs: intrinsic motivation beats parental pressure.',
    whyItMatters: 'A framework for deciding which programs are worth paying for.',
  },
  {
    name: 'Whose Game Is It, Anyway?',
    by: 'Richard Ginsburg, Stephen Durant & Amy Baltzell',
    meta: '2006',
    blurb: 'An age-by-age guide to what to expect from your child, yourself and the sports environment.',
  },
  {
    name: 'Game On: The All-American Race to Make Champions of Our Children',
    by: 'Tom Farrey',
    meta: '2008',
    blurb: "The founding critique of early elite tracking — and the book that led to the Aspen Institute's Project Play.",
  },
  {
    name: 'Range: Why Generalists Triumph in a Specialized World',
    by: 'David Epstein',
    meta: '2019',
    blurb: 'The evidence that sampling many sports beats early specialization (a Young Readers edition exists).',
    whyItMatters: 'A case for not paying for year-round, single-sport training at age ten.',
  },
  {
    name: 'Mindset: The New Psychology of Success',
    by: 'Carol S. Dweck',
    meta: '2006, updated 2016',
    blurb: 'Growth versus fixed mindset, with a full chapter on the mindset of a champion.',
  },
  {
    name: 'The Matheny Manifesto',
    by: 'Mike Matheny with Jerry B. Jenkins',
    meta: '2015',
    blurb: 'Grew out of a viral letter to youth-baseball parents: let coaches coach, let kids play.',
  },
  {
    name: 'Raising Empowered Athletes',
    by: 'Kirsten Jones',
    meta: '2023',
    blurb: 'First sports experiences, club and travel teams, family goal-setting and the move to high school sports.',
  },
  {
    name: "The Champion's Mind: How Great Athletes Think, Train, and Thrive",
    by: 'Jim Afremow',
    meta: '2014',
    blurb: 'The mental routines elite athletes use — a good one to hand to the athlete.',
  },
];
export const organizations: CuratedItem[] = [
  {
    name: 'Aspen Institute Project Play',
    url: 'https://projectplay.org',
    blurb: 'Parent checklists, the annual State of Play reports, and the national survey of what families spend on youth sports.',
  },
  {
    name: 'Positive Coaching Alliance',
    url: 'https://positivecoach.org',
    blurb: 'Free articles and videos for sports parents and coaches through its Development Zone.',
  },
  {
    name: 'TrueSport (USADA)',
    url: 'https://truesport.org',
    blurb: 'Free lessons and toolkits on sportsmanship, nutrition, clean sport and mental wellness.',
  },
  {
    name: 'Changing the Game Project',
    url: 'https://changingthegameproject.com',
    blurb: "Free articles for parents and coaches, plus the Way of Champions podcast.",
  },
  {
    name: 'U.S. Center for SafeSport',
    url: 'https://uscenterforsafesport.org',
    blurb: 'Free abuse-prevention training for parents, and guidance on what policies your club must follow.',
  },
  {
    name: 'CDC HEADS UP',
    url: 'https://www.cdc.gov/heads-up/',
    blurb: 'Free concussion training and fact sheets for parents and coaches.',
  },
  {
    name: 'NFHS Learning Center',
    url: 'https://nfhslearn.com',
    blurb: 'Free courses on concussion, heat illness and sudden cardiac arrest from the high-school federation.',
  },
  {
    name: 'The Hidden Opponent',
    url: 'https://thehiddenopponent.org',
    blurb: 'A nonprofit for student-athlete mental health, with resources for athletes and families.',
  },
  {
    name: 'NCAA Eligibility Center',
    url: 'https://web3.ncaa.org/ecwr3/',
    blurb: "A free profile page for high-school athletes thinking about college sports — check it before paying a recruiting service for the same thing.",
  },
];
